<template>
  <section class="order-page">
    <div class="page-header">
      <div>
        <h2>订单管理</h2>
        <p>统一维护客户物流订单，前台仅允许查询此处已维护的订单号或提单号。</p>
      </div>
    </div>

    <div class="toolbar-card">
      <div class="toolbar-grid">
        <el-select v-model="filters.companyId" clearable filterable placeholder="选择客户">
          <el-option v-for="item in customerOptions" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
        <el-input
          v-model="filters.keyword"
          clearable
          placeholder="搜索订单号 / 提单号"
          @keyup.enter="searchOrders"
        />
      </div>
      <div class="toolbar-actions">
        <el-button type="primary" @click="searchOrders">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
        <TableColumnSettings table-key="account-order" :columns="orderTableColumns" @update="columns => orderConfiguredColumns = columns" />
        <el-button type="primary" plain @click="openOrderDialog()">新增订单</el-button>
      </div>
    </div>

    <div class="table-card">
      <el-table v-loading="orderLoading" :data="orderList" border>
        <el-table-column
          v-for="column in visibleOrderColumns"
          :key="column.key"
          :prop="column.prop"
          :label="column.label"
          :width="column.width"
          :min-width="column.minWidth"
          :show-overflow-tooltip="column.showOverflowTooltip"
        >
          <template #default="{ row }">
            <span v-if="column.key === 'createTime'">{{ formatTime(row.createTime) }}</span>
            <span v-else>{{ row[column.prop] || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openOrderDialog(row)">编辑</el-button>
            <el-button link type="danger" @click="deleteOrder(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <Pagination :page="page" :size="size" :total="total" @current-change="handlePageChange" />

    <el-dialog v-model="orderDialog.visible" :title="orderDialog.form.id ? '编辑订单' : '新增订单'" width="620px" destroy-on-close>
      <el-form label-position="top">
        <el-form-item label="客户" required>
          <el-select
            v-model="orderDialog.form.companyId"
            clearable
            filterable
            placeholder="请选择客户"
            :disabled="Boolean(orderDialog.form.id)"
          >
            <el-option v-for="item in customerOptions" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="订单号">
          <el-input v-model="orderDialog.form.orderNo" placeholder="请输入订单号，可为空" />
        </el-form-item>
        <el-form-item label="提单号" required>
          <el-input v-model="orderDialog.form.blNo" placeholder="请输入提单号" />
        </el-form-item>
        <el-form-item label="承运人">
          <el-select
            v-model="orderDialog.form.carrierCode"
            clearable
            filterable
            placeholder="请选择承运人"
          >
            <el-option v-for="item in carrierOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="起运港" required>
          <el-select
            v-model="orderDialog.form.polCode"
            filterable
            remote
            reserve-keyword
            clearable
            placeholder="请输入港口中文 / 英文 / 代码搜索"
            :remote-method="searchPortOptions"
            :loading="portOptionsLoading"
          >
            <el-option
              v-for="item in portOptions"
              :key="item.portCode"
              :label="formatPortOption(item)"
              :value="item.portCode"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="orderDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="orderDialog.loading" @click="submitOrder">保存</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as Request from '@/assets/request'
import Pagination from '@/components/spagination'
import TableColumnSettings from '@/components/tableColumnSettings'
import { getVisibleColumns } from '@/utils/tableColumns'

const route = useRoute()

const filters = reactive({
  companyId: null,
  keyword: '',
})

const customerOptions = ref([])
const carrierOptions = ref([])
const orderList = ref([])
const orderLoading = ref(false)
const page = ref(1)
const size = ref(10)
const total = ref(0)
const portOptions = ref([])
const portOptionsLoading = ref(false)
const orderTableColumns = [
  { key: 'companyName', prop: 'companyName', label: '客户名称', minWidth: 220, showOverflowTooltip: true },
  { key: 'orderNo', prop: 'orderNo', label: '订单号', minWidth: 150 },
  { key: 'blNo', prop: 'blNo', label: '提单号', minWidth: 180, showOverflowTooltip: true },
  { key: 'createTime', label: '创建时间', width: 170 },
  { key: 'createUser', prop: 'createUser', label: '创建人', width: 120 },
]
const orderConfiguredColumns = ref([])
const visibleOrderColumns = computed(() => getVisibleColumns(orderConfiguredColumns.value))

const orderDialog = reactive({
  visible: false,
  loading: false,
  form: {
    id: null,
    companyId: null,
    orderNo: '',
    blNo: '',
    carrierCode: '',
    polCode: '',
  },
})

const formatTime = (time) => {
  if (!time) {
    return '-'
  }
  const date = new Date(Number(time))
  if (Number.isNaN(date.getTime())) {
    return '-'
  }
  const pad = (value) => `${value}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const formatPortOption = (item) => {
  if (!item) {
    return '-'
  }
  return [item.portCnName, item.portEnName, item.portCode].filter(Boolean).join(' / ')
}

const syncCompanyIdFromRoute = () => {
  const routeCompanyId = route.query.companyId ? Number(route.query.companyId) : null
  filters.companyId = Number.isFinite(routeCompanyId) ? routeCompanyId : null
}

const fetchCustomerOptions = async () => {
  const result = await Request.post({
    url: '/company/customer/options',
    data: {},
  })
  if (result?.status === 200) {
    customerOptions.value = result.data || []
  }
}

const fetchCarrierOptions = async () => {
  const result = await Request.post({
    url: '/company/order/carrierOptions',
    data: {},
  })
  if (result?.status === 200) {
    carrierOptions.value = result.data || []
  }
}

const fetchOrders = async () => {
  orderLoading.value = true
  const result = await Request.post({
    url: '/company/order/query',
    data: {
      companyId: filters.companyId || undefined,
      keyword: filters.keyword?.trim() || undefined,
      page: page.value,
      size: size.value,
    },
  })
  orderLoading.value = false
  if (result?.status === 200) {
    orderList.value = result.data.list || []
    total.value = result.data.total || 0
  }
}

const searchOrders = () => {
  page.value = 1
  fetchOrders()
}

const resetFilters = () => {
  filters.companyId = null
  filters.keyword = ''
  searchOrders()
}

const handlePageChange = (current) => {
  page.value = current
  fetchOrders()
}

const searchPortOptions = async (keyword = '') => {
  portOptionsLoading.value = true
  const result = await Request.post({
    url: '/port/query',
    data: {
      portName: keyword || undefined,
      page: 1,
      size: 20,
    },
  })
  portOptionsLoading.value = false
  if (result?.status === 200) {
    portOptions.value = result.data.list || []
  }
}

const ensurePortOption = (order = {}) => {
  if (!order.polCode) {
    return
  }
  const exists = portOptions.value.some((item) => item.portCode === order.polCode)
  if (!exists) {
    portOptions.value = [
      {
        portCode: order.polCode,
        portCnName: order.polCnName,
        portEnName: order.polEnName,
      },
      ...portOptions.value,
    ]
  }
}

const openOrderDialog = async (row) => {
  orderDialog.visible = true
  orderDialog.form = {
    id: row?.id || null,
    companyId: row?.companyId || filters.companyId || null,
    orderNo: row?.orderNo || '',
    blNo: row?.blNo || '',
    carrierCode: row?.carrierCode || '',
    polCode: row?.polCode || '',
  }
  if (row?.polCode) {
    ensurePortOption(row)
  } else {
    await searchPortOptions()
  }
}

const submitOrder = async () => {
  const payload = {
    id: orderDialog.form.id || undefined,
    companyId: orderDialog.form.companyId,
    orderNo: orderDialog.form.orderNo?.trim() || undefined,
    blNo: orderDialog.form.blNo?.trim(),
    carrierCode: orderDialog.form.carrierCode?.trim() || undefined,
    polCode: orderDialog.form.polCode,
  }
  if (!payload.companyId || !payload.blNo || !payload.polCode) {
    ElMessage.error('请选择客户，并填写提单号和起运港')
    return
  }
  orderDialog.loading = true
  const result = await Request.post({
    url: '/company/customer/order/save',
    data: payload,
  })
  orderDialog.loading = false
  if (result?.status === 200) {
    ElMessage.success('订单保存成功')
    orderDialog.visible = false
    fetchOrders()
  }
}

const deleteOrder = async (row) => {
  try {
    await ElMessageBox.confirm('确认删除该订单？删除后用户将无法查询此订单物流。', '删除订单', {
      type: 'warning',
    })
  } catch (error) {
    return
  }
  const result = await Request.post({
    url: '/company/customer/order/delete',
    data: {
      companyId: row.companyId,
      orderId: row.id,
    },
  })
  if (result?.status === 200) {
    ElMessage.success('订单已删除')
    fetchOrders()
  }
}

watch(
  () => route.query.companyId,
  () => {
    syncCompanyIdFromRoute()
    page.value = 1
    fetchOrders()
  }
)

onMounted(() => {
  syncCompanyIdFromRoute()
  fetchCustomerOptions()
  fetchCarrierOptions()
  fetchOrders()
})
</script>

<style scoped lang="less">
.order-page {
  .page-header {
    margin-bottom: 20px;

    h2 {
      margin-bottom: 4px;
      font-size: 20px;
      line-height: 28px;
      color: #1f2937;
    }

    p {
      font-size: 13px;
      color: #6b7280;
    }
  }

  .toolbar-card,
  .table-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  }

  .toolbar-card {
    padding: 12px 16px;
    margin-bottom: 12px;
  }

  .toolbar-grid {
    display: grid;
    grid-template-columns: 1.4fr 1.6fr;
    gap: 12px;
    align-items: center;
  }

  .toolbar-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }

  .table-card {
    padding: 12px;
  }

  :deep(.el-form-item__label) {
    font-size: 13px;
    color: #4b5563;
    padding-bottom: 4px;
  }

  :deep(.el-input__inner),
  :deep(.el-input) {
    font-size: 13px;
  }

  @media (max-width: 900px) {
    .toolbar-grid {
      grid-template-columns: 1fr;
    }

    .toolbar-actions {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>
